class CombBundleColors {
  constructor() {
    this.bundleVariants = this.readBundleVariants();
    this.selectors = '[data-comb-color-select]';
    this.radioSelector = '.comb-bundle-card input[type="radio"]';
    this.cardSelector = '[data-comb-bundle-card]';
    this.boundSelects = new WeakSet();
    this.syncFrame = null;
    this.defaultBundleIndex = 1;
    this.hasAppliedDefaultBundle = false;
    this.init();
  }

  init() {
    this.selectDefaultBundle();
    this.bindEvents();
    this.observeDom();
    this.syncAll();
  }

  bindEvents() {
    document.addEventListener('click', (event) => {
      const target = event.target;
      const select = target instanceof Element ? target.closest(this.selectors) : null;
      if (select instanceof HTMLSelectElement) {
        return;
      }

      const card = target instanceof Element ? target.closest(this.cardSelector) : null;
      if (card instanceof HTMLElement) {
        this.selectCard(card);
      }
    });

    document.addEventListener('keydown', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.matches(`${this.cardSelector} input[type="radio"]`)) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      const card = target.closest(this.cardSelector);
      if (card instanceof HTMLElement) this.selectCard(card);
    });

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.matches(this.radioSelector)) {
        this.scheduleSync();
        event.stopImmediatePropagation();
      }
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.querySelector('[data-comb-bundle-color-properties]')) return;
      this.syncAll();
      if (this.validate(form) && this.syncProductFormVariant(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    document.addEventListener('shopify:section:load', () => {
      this.bundleVariants = this.readBundleVariants();
      this.hasAppliedDefaultBundle = false;
      this.selectDefaultBundle();
      this.syncAll();
    });
  }

  bindSelect(select) {
    if (this.boundSelects.has(select)) return;
    this.boundSelects.add(select);

    const handleSelectEvent = () => {
      this.handleSelectUpdate(select);
    };

    select.addEventListener('input', handleSelectEvent);
    select.addEventListener('change', handleSelectEvent);
  }

  handleSelectUpdate(select) {
    this.updateSwatch(select);
    this.syncAll();
    this.hideError();
  }

  selectCard(card) {
    const radio = card.querySelector('input[type="radio"]');
    if (!(radio instanceof HTMLInputElement)) return;
    if (radio.disabled || radio.getAttribute('aria-disabled') === 'true') return;

    if (!radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.scheduleSync();
    }

    this.syncCardAccessibility();
  }

  selectDefaultBundle() {
    if (this.hasAppliedDefaultBundle) return;

    const radios = Array.from(document.querySelectorAll(this.radioSelector));
    const defaultRadio = radios[this.defaultBundleIndex];
    if (!(defaultRadio instanceof HTMLInputElement)) return;
    if (defaultRadio.disabled || defaultRadio.getAttribute('aria-disabled') === 'true') return;

    radios.forEach((radio) => {
      radio.checked = radio === defaultRadio;
      radio.dataset.currentChecked = radio === defaultRadio ? 'true' : 'false';
      radio.dataset.previousChecked = 'false';
    });

    this.hasAppliedDefaultBundle = true;
  }

  observeDom() {
    const observer = new MutationObserver(() => {
      this.scheduleSync();
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'data-current-checked', 'data-previous-checked'],
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  scheduleSync() {
    if (this.syncFrame) return;
    this.syncFrame = window.requestAnimationFrame(() => {
      this.syncFrame = null;
      this.syncAll();
    });
  }

  getActiveCard() {
    const checked = document.querySelector(`${this.radioSelector}:checked`);
    return checked?.closest('.comb-bundle-card') || null;
  }

  getActiveUnits() {
    const card = this.getActiveCard();
    const holder = card?.querySelector('[data-comb-bundle-colors]');
    return Number(holder?.dataset.combBundleUnits || 1);
  }

  syncAll() {
    document.querySelectorAll(this.selectors).forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      this.bindSelect(select);
      this.updateSwatch(select);
    });
    this.syncActiveCard();
    this.syncProperties();
    this.syncProductFormVariant();
    this.syncStickyCart();
  }

  syncActiveCard() {
    document.querySelectorAll('[data-comb-bundle-colors]').forEach((holder) => {
      const card = holder.closest('.comb-bundle-card');
      const isActive = Boolean(card?.querySelector('input[type="radio"]:checked'));
      holder.toggleAttribute('hidden', !isActive);
    });
    this.syncCardAccessibility();
  }

  syncCardAccessibility() {
    document.querySelectorAll(this.cardSelector).forEach((card) => {
      const radio = card.querySelector('input[type="radio"]');
      if (!(radio instanceof HTMLInputElement)) return;
      card.toggleAttribute('data-comb-card-active', radio.checked);
    });
  }

  syncProperties() {
    const holder = document.querySelector('[data-comb-bundle-color-properties]');
    const form = holder?.closest('form');
    if (!form) return;
    form.querySelectorAll('[data-comb-color-property]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.disabled = true;
    });
  }

  syncProductFormVariant(form = null) {
    const productForm = this.getProductForm(form);
    if (!(productForm instanceof HTMLFormElement)) return false;

    const variantInput = productForm.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return false;

    const bundleVariant = this.getBundleVariant();
    const variantId = bundleVariant?.id ? String(bundleVariant.id) : '';
    if (!variantId) {
      this.setBundleAddToCartState(productForm, false);
      return false;
    }

    variantInput.value = variantId;
    const available = bundleVariant.available !== false;
    this.setBundleAddToCartState(productForm, available);
    return available;
  }

  resolveForProductForm(scope = null) {
    const productForm = this.getProductForm(scope);
    if (!(productForm instanceof HTMLFormElement)) return null;
    if (!productForm.querySelector('[data-comb-bundle-color-properties]')) return null;

    const valid = this.validate(productForm);
    const available = valid && this.syncProductFormVariant(productForm);
    const bundleVariant = this.getBundleVariant();

    return {
      available,
      key: this.getBundleKey(),
      variantId: available && bundleVariant?.id ? String(bundleVariant.id) : null,
    };
  }

  getProductForm(scope = null) {
    if (scope instanceof HTMLFormElement) return scope;
    if (scope instanceof HTMLElement) {
      const form = scope.matches('product-form-component')
        ? scope.querySelector('form')
        : scope.closest('form') || scope.querySelector('form');
      if (form instanceof HTMLFormElement) return form;
    }
    return document.querySelector('[data-comb-bundle-color-properties]')?.closest('form') || null;
  }

  setBundleAddToCartState(productForm, available) {
    const addText = '\u05d4\u05d5\u05e1\u05e4\u05d4 \u05dc\u05e1\u05dc';
    const soldOutText = '\u05d0\u05d6\u05dc \u05de\u05d4\u05de\u05dc\u05d0\u05d9';
    const scope = productForm.closest('product-form-component') || productForm;
    const buttons = scope.querySelectorAll('[ref="addToCartButton"], .add-to-cart-button');

    scope.querySelectorAll('add-to-cart-component').forEach((component) => {
      if (available && typeof component.enable === 'function') component.enable();
      if (!available && typeof component.disable === 'function') component.disable();
    });

    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = !available;
      if (available) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', 'true');
      }
      this.setButtonLabel(button, available ? addText : soldOutText);
    });
  }

  setButtonLabel(button, label) {
    const content = button.querySelector('.add-to-cart-text__content > span:first-child');
    if (content instanceof HTMLElement) {
      content.textContent = label;
      return;
    }

    const text = button.querySelector('.add-to-cart-text__content, .button-text');
    if (text instanceof HTMLElement) {
      text.textContent = label;
      return;
    }

    let replaced = false;
    button.childNodes.forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) return;
      if (!node.textContent?.trim()) return;
      node.textContent = ` ${label} `;
      replaced = true;
    });

    if (!replaced) {
      button.append(document.createTextNode(` ${label} `));
    }
  }

  getSelectedColors(units = this.getActiveUnits()) {
    const activeCard = this.getActiveCard();
    const colors = [];

    for (let index = 1; index <= units; index += 1) {
      const select = activeCard?.querySelector(`[data-comb-color-select="${index}"]`);
      const fallback = select?.options?.[0]?.value || 'לוונדר';
      colors.push(select instanceof HTMLSelectElement ? select.value || fallback : fallback);
    }

    return colors;
  }

  getBundleKey() {
    const units = this.getActiveUnits();
    return [String(units), ...this.getSelectedColors(units)].join('|');
  }

  getBundleVariant() {
    return this.bundleVariants.get(this.getBundleKey()) || null;
  }

  getBundleVariantId() {
    return this.getBundleVariant()?.id || null;
  }

  readBundleVariants() {
    const script = document.querySelector('[data-comb-bundle-variants]');
    if (!(script instanceof HTMLScriptElement)) return new Map();

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      return new Map(Object.entries(parsed));
    } catch (error) {
      console.warn('[comb-bundle-colors] Failed to read bundle variant data', error);
      return new Map();
    }
  }

  syncStickyCart() {
    const card = this.getActiveCard();
    const stickyCart = document.querySelector('sticky-add-to-cart.sticky-add-to-cart--comb');
    if (!card || !stickyCart) return;

    const bundleVariant = this.getBundleVariant();
    const available = Boolean(bundleVariant && bundleVariant.available !== false);
    const title = card.querySelector('.comb-bundle-card__copy strong')?.textContent?.trim();
    const price = card.querySelector('.comb-bundle-card__price > span')?.textContent?.trim();
    const comparePrice = card.querySelector('.comb-bundle-card__price s')?.textContent?.trim();
    const stickyVariant = stickyCart.querySelector('.sticky-add-to-cart__variant');
    const stickyCurrentPrice = stickyCart.querySelector('.sticky-add-to-cart__price-current');
    const stickyComparePrice = stickyCart.querySelector('.sticky-add-to-cart__price-compare');
    const stickyButton = stickyCart.querySelector('[ref="addToCartButton"]');

    if (title && stickyVariant instanceof HTMLElement) stickyVariant.textContent = title;
    if (price && stickyCurrentPrice instanceof HTMLElement) stickyCurrentPrice.textContent = price;
    if (comparePrice && stickyComparePrice instanceof HTMLElement) stickyComparePrice.textContent = comparePrice;
    stickyCart.dataset.variantAvailable = available ? 'true' : 'false';
    if (stickyButton instanceof HTMLButtonElement) {
      stickyButton.disabled = !available;
      stickyButton.toggleAttribute('aria-disabled', !available);
    }
  }

  updateSwatch(select) {
    const option = select.selectedOptions[0];
    const swatch = select.closest('[data-comb-color-wrap]')?.querySelector('[data-comb-color-swatch]');
    if (swatch instanceof HTMLElement) {
      swatch.style.setProperty('--comb-color-swatch', option?.dataset.swatch || '#d9cfee');
    }
  }

  validate(form) {
    const units = this.getActiveUnits();
    const activeCard = this.getActiveCard();
    for (let index = 1; index <= units; index += 1) {
      const select = activeCard?.querySelector(`[data-comb-color-select="${index}"]`);
      const value = select instanceof HTMLSelectElement ? select.value : '';
      if (value) continue;
      this.showError(form, index);
      return false;
    }
    this.hideError(form);
    this.syncProperties();
    return true;
  }

  showError(form, unit) {
    const error = form.querySelector('[data-comb-color-error]');
    if (error instanceof HTMLElement) error.hidden = false;
    const activeCard = this.getActiveCard();
    const select = activeCard?.querySelector(`[data-comb-color-select="${unit}"]`);
    if (select instanceof HTMLSelectElement) {
      select.focus({ preventScroll: true });
      select.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  hideError(form = document) {
    const error = form.querySelector('[data-comb-color-error]');
    if (error instanceof HTMLElement) error.hidden = true;
  }
}

if (!window.__combBundleColors) {
  window.__combBundleColors = new CombBundleColors();
}
